/* eslint-disable react/no-unknown-property */

import { ComponentResource } from '@/api/serverApi'
import { useDispatch, useSelector } from 'react-redux'
import { setSelectedComponent } from '@/stores/active-design'
import { PropertyTabs, setCurrentPropertiesTab } from '@/stores/selected-properties-tab'
import { useMemo, useState } from 'react'
import { PMREMGenerator, Vector3 } from 'three'
import { RootState } from '@/stores'
import { ThreeColors } from '@/helpers/threeColors'
import { useThree } from '@react-three/fiber'

const anchorChannelDepthPadding = 5

type PropTypes = {
  channel: ComponentResource
  anchorSpacing: number
}

const AnchorModel = ({ channel, anchorSpacing }: PropTypes) => {
  const dispatch = useDispatch()
  const three = useThree()

  const productVariants = useSelector((state: RootState) => state.products.productVariants)

  const selectedCameraControlType = useSelector((state: RootState) => state.selectedCameraControlType.option)

  const channelVariant = productVariants?.find(p => p.id === channel?.product_variant_id)

  const isSelected = useSelector((state: RootState) => state.activeDesign.selectedComponentId  === channel.id)

  const [isHovered, setIsHovered] = useState(false)

  const geometry = useMemo(() => {
    const anchorHeadRadius = (channelVariant?.cast_in_anchor_channel?.anchor_head_diameter ?? 0) / 2
    const anchorShaftRadius = (channelVariant?.cast_in_anchor_channel?.anchor_shaft_diameter ?? 0) / 2

    const anchorHeadThickness = (channelVariant?.cast_in_anchor_channel?.anchor_head_thickness ?? 0)
    const anchorShaftLength = (channelVariant?.cast_in_anchor_channel?.anchor_length ?? 0) - anchorHeadThickness

    const head = (
      <cylinderGeometry
        args={[
          anchorHeadRadius,
          anchorHeadRadius,
          anchorHeadThickness,
        ]}
      />
    )

    const body = (
      <cylinderGeometry
        args={[
          anchorShaftRadius,
          anchorShaftRadius,
          anchorShaftLength,
        ]}
      />
    )

    return {
      head,
      body,
    }
  }, [channelVariant])

  const anchorPosition = useMemo(() => {
    const anchorLength = (channelVariant?.cast_in_anchor_channel?.anchor_length ?? 0)

    return new Vector3(
      0 + anchorSpacing,
      -(anchorLength / 2) + anchorChannelDepthPadding,
      0,
    )
  }, [anchorSpacing, channelVariant])

  const anchorHeadPosition = useMemo(() => {
    const anchorHeadThickness = (channelVariant?.cast_in_anchor_channel?.anchor_head_thickness ?? 0)
    const anchorShaftLength = (channelVariant?.cast_in_anchor_channel?.anchor_length ?? 0) - anchorHeadThickness

    return new Vector3(
      0,
      -(anchorShaftLength / 2),
      0,
    )
  }, [channelVariant])

  const envMap = useMemo(() => {
    const generator = new PMREMGenerator(three.gl).fromScene(three.scene)
    return generator.texture
  }, [three])

  return (
    <group
      position={anchorPosition}
      onClick={(e) => {
        e.stopPropagation()

        if (selectedCameraControlType !== 'cursor') {
          return
        }

        dispatch(setSelectedComponent(channel.id))
        dispatch(setCurrentPropertiesTab(PropertyTabs.Selected))
      }}
      onPointerEnter={(e) => {
        e.stopPropagation()

        if (selectedCameraControlType !== 'cursor') {
          return
        }

        setIsHovered(true)
      }}
      onPointerLeave={(e) => {
        e.stopPropagation()

        if (selectedCameraControlType !== 'cursor') {
          return
        }

        setIsHovered(false)
      }}
    >
      <mesh>
        { geometry.body }
        <meshPhysicalMaterial
          ior={1.52}
          specularIntensity={0.5}
          roughness={0.3}
          metalness={0.4}
          envMap={envMap}
          color={
            isSelected
              ? ThreeColors.selectedColor
              : (
                isHovered
                  ? [0.1, 0.1, 0.1]
                  : [0.15, 0.15, 0.15]
              )
          }
        />
      </mesh>
      <mesh
        position={anchorHeadPosition}
      >
        { geometry.head }
        <meshPhysicalMaterial
          ior={1.52}
          specularIntensity={0.5}
          roughness={0.3}
          metalness={0.4}
          envMap={envMap}
          color={
            isSelected
              ? ThreeColors.selectedColor
              : (
                isHovered
                  ? [0.1, 0.1, 0.1]
                  : [0.15, 0.15, 0.15]
              )
          }
        />
      </mesh>
    </group>
  )
}

export default AnchorModel
